import React, { useEffect, useState, useCallback, useRef } from "react";
import { useConnectedUser } from "../../context/connected-user.context";
import {
  connectWallet,
  getCurrentWalletConnected,
  getMetaMaskProvider,
  getProviderWithEIP6963,
  isMobile,
  isAndroid,
  isInAppBrowser,
} from "../../web3/connectWallet";
import { Button } from "../../design-system/button";

export const ConnectWalletButton: React.FC<{ autologin?: boolean }> = ({
  autologin,
}) => {
  const [walletAddress, setWalletAddress] = useState<string | undefined>(undefined);
  const [availableAddresses, setAvailableAddresses] = useState<string[]>([]);
  const [showAddressSelection, setShowAddressSelection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isOnMobile, setIsOnMobile] = useState(false);
  const [isOnAndroid, setIsOnAndroid] = useState(false);
  const [isInApp, setIsInApp] = useState(false);
  const [connectionAttempted, setConnectionAttempted] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  
  const connectedUserContext = useConnectedUser();
  
  // Use refs to track listener state and prevent duplicate listeners
  const listenersAddedRef = useRef(false);
  const providerRef = useRef<any>(null);
  const isManualDisconnectRef = useRef(false);
  
  // Check if we were redirected back from MetaMask mobile app
  const checkRedirectFromMetaMask = useCallback(() => {
    if (typeof window !== 'undefined') {
      const returnToUrl = sessionStorage.getItem('returnToUrl');
      if (returnToUrl) {
        console.debug("Detected return from MetaMask mobile");
        sessionStorage.removeItem('returnToUrl');
        // Wait a moment for any MetaMask injections to complete
        setTimeout(() => {
          if (!isManualDisconnectRef.current) {
            init();
          }
        }, 500);
      }
    }
  }, []);
  
  useEffect(() => {
    // Detect device types and environments
    setIsOnMobile(isMobile());
    setIsOnAndroid(isAndroid());
    setIsInApp(isInAppBrowser());
    
    // Set wallet address from context when it changes
    setWalletAddress(connectedUserContext.connectedUser?.walletAddress);
    
    // Check if we're returning from MetaMask mobile
    checkRedirectFromMetaMask();
  }, [connectedUserContext.connectedUser, checkRedirectFromMetaMask]);
  
  // Cleanup function to remove all listeners
  const removeWalletListeners = useCallback(async () => {
    if (providerRef.current && listenersAddedRef.current) {
      console.debug("Removing wallet listeners");
      try {
        if (providerRef.current.removeListener) {
          providerRef.current.removeListener("accountsChanged", handleAccountsChanged);
          providerRef.current.removeListener("chainChanged", handleChainChanged);
          providerRef.current.removeListener("disconnect", handleDisconnect);
        }
        listenersAddedRef.current = false;
        providerRef.current = null;
      } catch (error) {
        console.error("Error removing listeners:", error);
      }
    }
  }, []);
  
  const init = async () => {
    if (isManualDisconnectRef.current || isDisconnecting) {
      console.debug("Manual disconnect in progress, skipping init");
      return;
    }
    
    try {
      setLoading(true);
      console.debug("Initializing wallet connection...");
      
      // Enhanced provider detection - try multiple methods
      let provider = await getProviderWithEIP6963();
      
      if (!provider) {
        provider = await getMetaMaskProvider();
      }
      
      if (!provider) {
        console.warn("No ethereum provider found during init");
        setLoading(false);
        return;
      }
      
      const { address, status } = await getCurrentWalletConnected();
      if (address && !isManualDisconnectRef.current) {
        console.debug("Wallet connected:", address);
        connectedUserContext.setConnectedUser(address);
        await addWalletListener(provider);
      } else {
        console.debug("No wallet connected during init", status);
        if (status) {
          setError(typeof status === 'string' ? status : "Unable to connect wallet");
        }
      }
    } catch (err) {
      console.error("Failed to connect wallet during init:", err);
      setError("Failed to connect wallet.");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    // Only run autoconnect once and not if manually disconnected
    if (autologin && !connectionAttempted && !isManualDisconnectRef.current) {
      setConnectionAttempted(true);
      init();
    } else if (!autologin) {
      console.debug("Autologin disabled, skipping auto wallet connection");
    }
  }, [autologin, connectionAttempted]);
  
  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      removeWalletListeners();
    };
  }, [removeWalletListeners]);
  
  const addWalletListener = async (provider?: any) => {
    try {
      // Use provided provider or get a new one
      let walletProvider = provider;
      if (!walletProvider) {
        walletProvider = await getProviderWithEIP6963();
        if (!walletProvider) {
          walletProvider = await getMetaMaskProvider();
        }
      }
      
      if (walletProvider && !listenersAddedRef.current) {
        console.debug("Adding ethereum account change listener");
        
        // Store provider reference
        providerRef.current = walletProvider;
        
        // Add fresh listeners
        if (walletProvider.on) {
          walletProvider.on("accountsChanged", handleAccountsChanged);
          walletProvider.on("chainChanged", handleChainChanged);
          walletProvider.on("disconnect", handleDisconnect);
          listenersAddedRef.current = true;
        }
      } else {
        console.debug("No ethereum provider to add listeners to or listeners already added");
      }
    } catch (error) {
      console.error("Error adding wallet listeners:", error);
    }
  };
  
  const handleAccountsChanged = (accounts: string[]) => {
    console.debug("Accounts changed:", accounts);
    // Don't auto-reconnect if user manually disconnected
    if (isManualDisconnectRef.current || isDisconnecting) {
      console.debug("Manual disconnect in progress, ignoring account change");
      return;
    }
    
    if (accounts.length > 0) {
      // Only update if it's different from current address
      const currentAddress = connectedUserContext.connectedUser?.walletAddress;
      if (currentAddress !== accounts[0]) {
        console.debug("Account changed to:", accounts[0]);
        connectedUserContext.setConnectedUser(accounts[0]);
        setError(undefined);
      }
    } else {
      console.debug("No accounts found, disconnecting user");
      connectedUserContext.disconnectUser();
    }
  };
  
  const handleChainChanged = (chainId: string) => {
    console.debug("Chain changed:", chainId);
    // Don't auto-reconnect if user manually disconnected
    if (isManualDisconnectRef.current || isDisconnecting) {
      console.debug("Manual disconnect in progress, ignoring chain change");
      return;
    }
    
    // Just make sure our connection is still valid
    refreshConnection();
  };
  
  const handleDisconnect = (error: any) => {
    console.debug("Disconnect event:", error);
    // Don't auto-reconnect if user manually disconnected
    if (!isManualDisconnectRef.current && !isDisconnecting) {
      connectedUserContext.disconnectUser();
      setError("Wallet disconnected.");
    }
  };
  
  const refreshConnection = async () => {
    if (isManualDisconnectRef.current || isDisconnecting) {
      console.debug("Manual disconnect in progress, skipping refresh");
      return;
    }
    
    try {
      const { address } = await getCurrentWalletConnected();
      if (address) {
        connectedUserContext.setConnectedUser(address);
      } else {
        connectedUserContext.disconnectUser();
      }
    } catch (err) {
      console.error("Error refreshing connection:", err);
    }
  };

  // Get all available addresses with better error handling
  const getAllAvailableAddresses = async () => {
    try {
      let provider = await getProviderWithEIP6963();
      
      if (!provider) {
        provider = await getMetaMaskProvider();
      }
      
      if (!provider) {
        throw new Error("No ethereum provider found");
      }

      console.debug("Requesting accounts from provider...");
      
      // Request access to all addresses
      const accounts = await provider.request({
        method: "eth_requestAccounts",
      });
      
      console.debug("Accounts received:", accounts);
      return accounts || [];
    } catch (error) {
      console.error("Error getting available addresses:", error);
      // Don't throw immediately, let the caller handle gracefully
      return [];
    }
  };
  
  const connectWalletHandler = async () => {
    // Reset manual disconnect flag when user actively tries to connect
    isManualDisconnectRef.current = false;
    
    try {
      setLoading(true);
      setError(undefined);
      // Clear any previous selection state
      setShowAddressSelection(false);
      setAvailableAddresses([]);
      
      // Special warning for in-app browsers
      if (isInApp) {
        setError("In-app browsers have limited support. For best results, open in your device's native browser.");
        setTimeout(() => {
          continueConnectWallet();
        }, 1500);
        return;
      }
      
      await continueConnectWallet();
    } catch (err) {
      console.error("Wallet connection error:", err);
      setError("Connection failed. Please try again.");
      setLoading(false);
    }
    // Note: Don't set loading to false here - let continueConnectWallet handle it
  };
  
  const continueConnectWallet = async () => {
    console.debug("Continuing wallet connection process");
    
    try {
      // Get all available addresses
      const addresses = await getAllAvailableAddresses();
      
      if (addresses.length === 0) {
        setError("No addresses found in MetaMask");
        setLoading(false);
        return;
      }
      
      if (addresses.length === 1) {
        // Single address, connect directly
        console.debug("Single address found, connecting directly:", addresses[0]);
        connectedUserContext.setConnectedUser(addresses[0]);
        await addWalletListener();
        setError(undefined);
        setLoading(false);
      } else {
        // Multiple addresses, show selection - don't set loading to false yet
        console.debug("Multiple addresses found:", addresses);
        setAvailableAddresses(addresses);
        setShowAddressSelection(true);
        setLoading(false); // Allow user interaction with selection
        setError(undefined);
      }
    } catch (error) {
      console.error("Error during wallet connection:", error);
      
      // Try to still get multiple addresses through the fallback method
      try {
        // First, try to get accounts directly from provider
        let provider = await getProviderWithEIP6963();
        if (!provider) {
          provider = await getMetaMaskProvider();
        }
        
        if (provider) {
          const accounts = await provider.request({ method: 'eth_requestAccounts' });
          
          if (accounts.length > 1) {
            console.debug("Multiple addresses found via fallback:", accounts);
            setAvailableAddresses(accounts);
            setShowAddressSelection(true);
            setLoading(false);
            setError(undefined);
            return;
          } else if (accounts.length === 1) {
            connectedUserContext.setConnectedUser(accounts[0]);
            await addWalletListener();
            setError(undefined);
            setLoading(false);
            return;
          }
        }
      } catch (fallbackError) {
        console.error("Fallback address detection failed:", fallbackError);
      }
      
      // Final fallback to old method
      const walletResponse = await connectWallet();
      
      if (walletResponse.address) {
        console.debug("Wallet connected successfully:", walletResponse.address);
        connectedUserContext.setConnectedUser(walletResponse.address);
        await addWalletListener();
        setError(undefined);
      } else if (walletResponse.status) {
        console.debug("Wallet connection status:", walletResponse.status);
        if (typeof walletResponse.status === 'string') {
          setError(walletResponse.status);
        } else {
          setError("Please install MetaMask to continue");
        }
      }
      setLoading(false);
    }
  };

  // Function to select a specific address
  const selectAddress = async (selectedAddress: string) => {
    try {
      setLoading(true);
      console.debug("Selected address:", selectedAddress);
      
      connectedUserContext.setConnectedUser(selectedAddress);
      await addWalletListener();
      setError(undefined);
      setShowAddressSelection(false);
      setAvailableAddresses([]);
    } catch (error) {
      console.error("Error selecting address:", error);
      setError("Failed to connect with selected address");
    } finally {
      setLoading(false);
    }
  };

  // Function to cancel address selection
  const cancelAddressSelection = () => {
    setShowAddressSelection(false);
    setAvailableAddresses([]);
    setLoading(false);
  };

  // Function to format address (show beginning and end)
  const formatAddress = (address: string) => {
    return `${address.substring(0, 6)}...${address.substring(38)}`;
  };
  
  const disconnectWalletHandler = async () => {
    console.debug("Disconnecting wallet");
    
    // Set flags to prevent auto-reconnection
    isManualDisconnectRef.current = true;
    setIsDisconnecting(true);
    
    try {
      // Remove listeners first
      await removeWalletListeners();
      
      // Clear UI state
      setShowAddressSelection(false);
      setAvailableAddresses([]);
      setError(undefined);
      
      // Disconnect from context
      connectedUserContext.disconnectUser();
      
      // Clear any stored session data
      sessionStorage.removeItem('returnToUrl');
      
    } catch (error) {
      console.error("Error during disconnect:", error);
    } finally {
      setIsDisconnecting(false);
      // Keep isManualDisconnectRef.current = true to prevent auto-reconnection
    }
  };
  
  const renderConnectButton = () => {
    if (loading && !showAddressSelection) {
      return <span>...connecting</span>;
    }
    
    // For Android devices
    if (isOnAndroid) {
      return <Button onClick={connectWalletHandler} text="Open in MetaMask" type="action" />;
    } 
    // For iOS devices
    else if (isOnMobile && /iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      return <Button onClick={connectWalletHandler} text="Connect with MetaMask" type="action" />;
    }
    // For other mobile devices
    else if (isOnMobile) {
      return <Button onClick={connectWalletHandler} text="Connect with MetaMask" type="action" />;
    }
    
    // For desktop
    return <Button onClick={connectWalletHandler} text="Connect Wallet" type="gradient" gradientColors={['#ff0000', '#ff8c00']} />;
  };

  // Render address selector with debugging
  const renderAddressSelection = () => {
    console.debug("Rendering address selection:", { 
      showAddressSelection, 
      availableAddresses: availableAddresses.length,
      loading 
    });
    
    if (!showAddressSelection) return null;

    return (
      <div className="border rounded-lg p-4 mt-2 bg-white shadow-lg">
        <h3 className="text-lg font-semibold mb-3">
          Select an address to connect ({availableAddresses.length} accounts found):
        </h3>
        <div className="space-y-2">
          {availableAddresses.map((address, index) => (
            <button
              key={address}
              onClick={() => selectAddress(address)}
              disabled={loading}
              className="w-full text-left p-3 border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm">{formatAddress(address)}</span>
                <span className="text-xs text-gray-500">Account {index + 1}</span>
              </div>
              <div className="text-xs text-gray-400 mt-1">{address}</div>
            </button>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <Button
            onClick={cancelAddressSelection}
            text="Cancel"
            type="action"
            disabled={loading}
          />
        </div>
        {loading && (
          <div className="text-center mt-2 text-sm text-gray-500">
            Connecting...
          </div>
        )}
      </div>
    );
  };
  
  // Show a special message for in-app browsers
  if (isInApp && !walletAddress) {
    return (
      <div>
        <p className="text-amber-500 text-sm mb-2">
          You're using an in-app browser which may not fully support wallet connections.
          For the best experience, please open this page in your device's native browser.
        </p>
        {renderConnectButton()}
        {renderAddressSelection()}
      </div>
    );
  }
  
  return (
    <div>
      {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
      
      {walletAddress ? (
        <>
          <Button
            text="Disconnect Wallet"
            onClick={disconnectWalletHandler}
            type="action"
            disabled={isDisconnecting}
          />
          <p>
            Connected: {walletAddress.substring(0, 6)}...
            {walletAddress.substring(38)}
          </p>
        </>
      ) : (
        <>
          {renderConnectButton()}
          {renderAddressSelection()}
          
          {!showAddressSelection && (
            <>
              {isOnAndroid && !walletAddress && (
                <p className="text-sm mt-2 text-blue-600">
                  Will open MetaMask app to connect
                </p>
              )}
              
              {isOnMobile && !isOnAndroid && !walletAddress && (
                <p className="text-sm mt-2">
                  You'll be redirected to MetaMask
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};